import { signOut } from "@/lib/sign-out";
import { AppSidebar } from "@/components/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { impersonationSession } from "@/lib/impersonation-session";
import { cn, currentEnvironment } from "@/lib/utils";

import {
  createFileRoute,
  Link,
  Outlet,
  useLocation,
  useRouter,
} from "@tanstack/react-router";
import { LogOut, Settings } from "lucide-react";
import { z } from "zod/v4";
import { zodValidator } from "@tanstack/zod-adapter";
import { ApplicationSelect } from "@/components/application-select";
import { datadogRum } from "@datadog/browser-rum";
import { getMerchantQuery, MerchantQuery } from "@/lib/merchant-query";
import { SessionExpirationCheck } from "@/routes/auth/_auth-layout/-components/auth/session-expiration-check";
import { OverdueInvoiceBanner } from "@/components/overdue-invoice-banner";
import { ComplianceDocumentsRefreshBanner } from "@/components/compliance-documents-refresh-banner";
import { ComplianceDocumentsRefreshDialog } from "@/components/compliance-documents-refresh-dialog";
import { OutstandingRfiBanner } from "@/components/compliance/outstanding-rfi-banner";
import { EmergencyNoticeBanner } from "@/modules/emergency-notice/emergency-notice-banner";
import { StablecoinsLiveBanner } from "@/modules/stablecoins/stablecoins-live-banner";
import {
  getJwtFromStorage,
  hasRole,
  parseVolumeJwt,
  redirectToSignIn,
  validateSession,
  validateUserSession,
} from "@/lib/auth";
import { KnownGenericError } from "@/lib/errors";
import { useMerchant } from "@/hooks/use-merchant";
import { useMerchantComplianceCountsRealtime } from "@/hooks/useMerchantComplianceCountsRealtime";
import { useEffect } from "react";
import { usePostHog } from "posthog-js/react";
import { useCanSeeFxBackedComplianceScreen } from "@/routes/(app)/_main-layout/_compliance/-components/is-compliance-enabled";

const searchParamsSchema = z.object({
  applicationId: z.string().optional(),
});

const getMerchantId = (tokenContents: ReturnType<typeof parseVolumeJwt>) => {
  const merchantId =
    tokenContents.merchantId ?? impersonationSession.get(tokenContents);
  if (!merchantId) {
    throw redirectToSignIn(false);
  }
  return merchantId;
};

const getSelectedApplication = (
  applications: MerchantQuery["applications"],
  applicationId?: string,
) => {
  const selectedApplication =
    applications.find((a) => a.applicationId === applicationId) ??
    applications.at(0);

  if (!selectedApplication) {
    throw new KnownGenericError({
      userFriendlyMessage: "Invalid application configuration",
    });
  }

  return selectedApplication;
};

const setupDatadogContext = (
  selectedApplication: MerchantQuery["applications"][number],
  loggedInUserEmail: string | null,
  companyName: string,
) => {
  datadogRum.setGlobalContextProperty("environment", currentEnvironment());
  datadogRum.setViewContextProperty(
    "applicationId",
    selectedApplication.applicationId,
  );
  datadogRum.setViewContextProperty("email", loggedInUserEmail);
  datadogRum.setViewContextProperty("merchant", companyName);
};

export const Route = createFileRoute("/(app)/_main-layout")({
  component: () => <LayoutComponent />,
  beforeLoad: async (params) => {
    const token = getJwtFromStorage();
    validateSession(token);

    const tokenContents = parseVolumeJwt(token);
    const loggedInUserEmail = validateUserSession(tokenContents);
    const merchantId = getMerchantId(tokenContents);

    const data = await params.context.queryClient.fetchQuery({
      ...getMerchantQuery({
        accessToken: token,
        merchantId: merchantId,
      }),
    });

    const selectedApplication = getSelectedApplication(
      data.applications,
      params.search.applicationId,
    );

    setupDatadogContext(
      selectedApplication,
      loggedInUserEmail,
      data.merchant.companyName,
    );

    return {
      accessToken: token,
      decodedJwt: tokenContents,
      merchantId: data.merchant.merchantId,
      email: loggedInUserEmail,
      authorities: tokenContents.authorities,
      selectedApplicationId: selectedApplication.applicationId,
      userId: tokenContents.sub,
      isAdmin: tokenContents.adminId !== undefined,
      hasVolumeAdminRole:
        hasRole("VOLUME_ADMIN", tokenContents.authorities) ||
        hasRole("VOLUME_ADMIN_READ", tokenContents.authorities),
    };
  },
  validateSearch: zodValidator(searchParamsSchema),
});

const MainLayoutContent = () => {
  const merchant = useMerchant();
  const context = Route.useRouteContext();
  const location = useLocation();
  const router = useRouter();
  const posthog = usePostHog();
  const canSeeFxBackedComplianceScreen = useCanSeeFxBackedComplianceScreen();

  const isAdmin = context.isAdmin;

  useMerchantComplianceCountsRealtime({
    accessToken: context.accessToken,
    applicationId: context.selectedApplicationId,
    enabled: canSeeFxBackedComplianceScreen,
  });

  useEffect(() => {
    posthog.setPersonProperties({
      applicationId: context.selectedApplicationId,
      merchantId: context.merchantId,
      merchantName: merchant.companyName,
      isAdmin: context.isAdmin,
    });
  }, []);

  return (
    <>
      <AppSidebar />
      <main
        className={cn(
          "bg-volume-grey min-w-[750px] flex-1",
          location.pathname.startsWith("/exchange")
            ? ""
            : "min-h-0 overflow-y-auto",
        )}
        style={{
          scrollbarGutter: "stable",
        }}
      >
        <div className="mt-2 mb-4 flex flex-1 flex-col pl-4 pr-4">
          <div className="flex flex-col">
            <div
              className={cn(
                "flex flex-wrap items-center gap-2 mb-2",
                location.pathname === "/admin" && "w-full",
              )}
            >
              <SidebarTrigger />
              {/* In the top bar, so it costs no height. The exchange is where it sends people;
                  it would only repeat it there. */}
              {location.pathname.startsWith("/exchange") ? null : (
                <StablecoinsLiveBanner />
              )}
              {(!isAdmin || (isAdmin && location.pathname !== "/admin")) && (
                <div className="ml-auto flex shrink-0 gap-4">
                  <ApplicationSelect
                    applications={merchant.applications ?? []}
                  />
                  <div className="flex-none">
                    <DropdownMenu>
                      <DropdownMenuTrigger className="min-h-9 rounded-lg border-2 border-solid bg-white focus:outline-hidden data-[state=open]:border-volume-text-purple">
                        <div
                          className={cn(
                            "flex items-center rounded-xl bg-white px-3 py-1",
                          )}
                        >
                          <div className="mr-3 max-w-[180px] min-w-0 break-words text-left font-normal">
                            {merchant.companyName}
                          </div>
                          <img
                            src={"/favicon.ico"}
                            width={28}
                            height={28}
                            alt=""
                            className="ml-2 shrink-0 rounded-full object-contain"
                          />
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        onCloseAutoFocus={undefined}
                        align="end"
                        className="rounded-xl border border-slate-200 shadow-lg shadow-slate-200/70"
                      >
                        <Link
                          to="/settings/account-settings"
                          search={(prev) =>
                            prev?.applicationId
                              ? { applicationId: prev.applicationId }
                              : {}
                          }
                          onMouseEnter={() => {
                            router.preloadRoute({
                              to: "/settings/account-settings",
                              search: (prev) =>
                                prev?.applicationId
                                  ? { applicationId: prev.applicationId }
                                  : {},
                            });
                          }}
                        >
                          <DropdownMenuItem className="hover:cursor-pointer">
                            <Settings className="mr-2" />
                            Settings
                          </DropdownMenuItem>
                        </Link>
                        <DropdownMenuItem
                          className="hover:cursor-pointer"
                          onClick={() => {
                            void signOut(context.queryClient, router);
                          }}
                        >
                          <div>
                            <LogOut className="mr-2 inline" />
                            Sign out
                          </div>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              )}
            </div>
          </div>
          <ComplianceDocumentsRefreshDialog />
          <EmergencyNoticeBanner />
          <OutstandingRfiBanner />
          <ComplianceDocumentsRefreshBanner />
          <OverdueInvoiceBanner />
          <Outlet />
        </div>
      </main>
    </>
  );
};

const LayoutComponent = () => {
  const context = Route.useRouteContext();
  const location = useLocation();
  const isExchangeRoute = location.pathname.startsWith("/exchange");

  return (
    <SidebarProvider
      className={
        isExchangeRoute
          ? "!static !h-auto min-h-svh !overflow-visible"
          : undefined
      }
    >
      <SessionExpirationCheck decodedJwt={context.decodedJwt} />
      <MainLayoutContent />
    </SidebarProvider>
  );
};
